import React from 'react';

import { Form, Field, useFormState } from 'react-final-form';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import FormSection from 'components/Layouts/Form/FormSection';
import LayoutWithFooter from 'components/Layouts/shared/LayoutWithFooter';
import Backdrop from 'components/shared/Backdrop';
import ErrorScreen from 'components/shared/ErrorScreen';
import FormGroupAdapter from 'components/shared/FormAdapters/FormDefault/FormGroup';
import { withFinalForm } from 'components/shared/FormAdapters/FormDefault/select/withFinalForm';
import FormErrorText from 'components/shared/FormErrorText';
import { GeneralPopoverPagePreview } from 'components/shared/GeneralPopoverPagePreview';
import Loader from 'components/shared/Loader';
import { Select } from 'components/shared/Select/adapters/FinalForm';
import { PageAsyncSelect } from 'components/shared/SelectRB/custom';
import Tooltip, { TooltipText } from 'components/shared/Tooltip';
import Text from 'components/shared/Typography/Text';
import UnsavedChangesFormWatcher from 'components/shared/UnsavedChangesFormWatcher';
import {
  PAGE_TYPE_ABOUT_SAFARI,
  PAGE_TYPE_ABOUT_US,
  PAGE_TYPE_ACTIVITY,
  PAGE_TYPE_MISC,
  PAGE_TYPE_PRE_TRAVEL_INFO,
} from 'constants/index';
import { useFormFieldPopup } from 'hooks/useFormFieldPopup';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';
import { useScrollPageToTop } from 'hooks/useScrollPageToTop';
import { itinerariesItemDataSelector } from 'store/itinerariesItem/selectors';
import { accountIdSelector } from 'store/user/selectors';
import { axiosGetAutoCancelDecorator } from 'utils/axiosGetAutoCancelDecorator';
import { reverseFlightDateValues } from 'utils/common';
import { isSample } from 'utils/itineraries/functions';
import {
  useItinerariesItemNextStepFormHandler,
  useItinerariesItemSaveHandler,
} from 'utils/itineraries/functions/builderUtils';
import { ITINERARY_STEPS } from 'utils/itineraries/itinerary/map';

import ItinerariesItemActionButtons from '../../ItinerariesItemBuilder/ActionButtons';
import ItinerariesItemNextButton from '../../ItinerariesItemBuilder/ItinerariesItemNextButton';
import { CustomAppsBlock } from '../../shared/Forms/CustomApps';
import WhiteLabeling from '../../shared/Forms/WhiteLabeling';
import { useTryToRemindCheckPrimaryImageForGeneralTab } from '../../shared/functions';
import ItinerarySearchFilterButton from '../../shared/SearchFilter';
import { prepareInitialValues, useGeneralInfo } from './functions';
import styles from './index.module.css';

const pageTypesToSearch = [
  PAGE_TYPE_ACTIVITY,
  PAGE_TYPE_MISC,
  PAGE_TYPE_ABOUT_SAFARI,
  PAGE_TYPE_ABOUT_US,
  PAGE_TYPE_PRE_TRAVEL_INFO,
];

const prepareSubmitValues = ({ app, ...values }, isSampleValues) => {
  const preparedValues = {
    ...values,

    customAppId: app.id,

    aboutUsPages: values.aboutUsPages ?? [],
    aboutSafariPages: values.aboutSafariPages ?? [],
    miscPages: values.miscPages ?? [],
    ...(values.tripEvents && {
      tripEvents: values.tripEvents.map((t) => {
        if (t.data?.isIDL) {
          return reverseFlightDateValues(t);
        }
        return t;
      }),
    }),
  };

  if (isSampleValues) {
    return preparedValues;
  }

  return {
    ...preparedValues,
    tourRequest: {
      id: typeof values.tourRequest.id === 'string' ? undefined : values.tourRequest.id,
      fileName: typeof values.tourRequest.id === 'string' ? values.tourRequest.id : undefined,
    },
  };
};

const decoratedAxiosGet = axiosGetAutoCancelDecorator();

const PageAsyncSelectAdapter = withFinalForm(PageAsyncSelect);

function GeneralInfo() {
  useScrollPageToTop();
  const { libType } = useParams();
  const itinerary = useSelector(itinerariesItemDataSelector);
  const { onAfterSave } = useTryToRemindCheckPrimaryImageForGeneralTab({
    itineraryItemName: 'itinerary',
    neededLastAvailableStep: ITINERARY_STEPS.LANDING,
  });
  const handleNextStep = useItinerariesItemNextStepFormHandler({
    showValidationError: true,
    onAfterNext: onAfterSave,
  });
  const isSampleItinerary = isSample(libType);
  const { isLoading, error, validate } = useGeneralInfo(isSampleItinerary);
  const handleSave = useItinerariesItemSaveHandler({
    showValidationError: true,
    onAfterSave,
  });
  const [isSaving, setIsSaving] = React.useState(false);

  if (isLoading) {
    return (
      <Backdrop isOpen>
        <Loader />
      </Backdrop>
    );
  }

  if (error) {
    return <ErrorScreen status="404" />;
  }

  const initialValues = prepareInitialValues(itinerary);

  return (
    <LayoutWithFooter>
      {({ footerComponent: FooterComponent }) => (
        <Form
          onSubmit={(values) => handleNextStep(prepareSubmitValues(values, isSampleItinerary))}
          initialValues={initialValues}
          validate={validate}
          destroyOnUnregister
          render={({ handleSubmit, submitting, submitError, hasValidationErrors, values }) => {
            return (
              <form onSubmit={handleSubmit} autoComplete="nope" className={styles.form}>
                <UnsavedChangesFormWatcher />

                {submitting && <Loader isFillBlock />}

                <FormSection type="rounded">
                  <InternalSection />
                </FormSection>

                <FormSection type="rounded">
                  <CoverSection />
                </FormSection>

                <FormSection type="rounded">
                  <IncludeSection />
                </FormSection>

                <FormSection type="rounded">
                  <WhiteLabeling />
                </FormSection>

                <FormSection type="rounded">
                  <CustomAppsBlock />
                </FormSection>

                {submitError && <FormErrorText>{submitError}</FormErrorText>}

                <FooterComponent>
                  <ItinerariesItemNextButton id="itineraryGeneralInfoNext" isSaving={isSaving} />
                </FooterComponent>

                <ItinerariesItemActionButtons
                  hasValidationErrors={hasValidationErrors}
                  submitting={submitting}
                  onSave={() => handleSave(prepareSubmitValues(values, isSampleItinerary))}
                  onIsSavingChange={setIsSaving}
                />
              </form>
            );
          }}
        />
      )}
    </LayoutWithFooter>
  );
}

const InternalSection = () => {
  const { libType } = useParams();
  const isSampleItinerary = isSample(libType);
  const itinerary = useSelector(itinerariesItemDataSelector);
  const accountId = useSelector(accountIdSelector);
  const showPopup = useFormFieldPopup();

  const loadFilenameOptions = async (query) => {
    const response = await decoratedAxiosGet(`/acc/${accountId}/tour_requests/lite`, { params: { name_q: query } });

    return response.data.data.map((item) => ({
      value: item.id,
      label: item.fileName,
    }));
  };

  return (
    <>
      <Text type="brandon" isStrong weight="500" className={styles.sectionTitle}>
        Internal Information
      </Text>

      {!isSampleItinerary && (
        <>
          <Tooltip
            id="itineraryGeneralInfoTooltip"
            arrowColor="#424242"
            backgroundColor="#424242"
            textColor="var(--tooltip-bg-color)"
            disable={!itinerary.confirmed}
          >
            <TooltipText>
              Sorry, the confirmed itinerary cannot be moved to another file.
              <br />
              You can reset the confirmed itinerary by moving the file back to the “Planning”
              <br />
              and then selecting a different itinerary to confirm.
            </TooltipText>
          </Tooltip>
          <Field
            data-tip
            data-for="itineraryGeneralInfoTooltip"
            name="tourRequest.id"
            component={FormGroupAdapter}
            customComponent={Select}
            placeholder="File Name*"
            loadOptions={loadFilenameOptions}
            async
            creatable
            allowCreateWhileLoading
            isValidNewOption={(input) => !!input}
            debounced
            debounceTimeout={500}
            optionToString
            editable
            readOnly={itinerary.confirmed}
            tooltipContent={
              <Text accent style={{ fontSize: '1.5rem' }}>
                <b>Tool Tip:</b>
                <br />
                <br />
                Set the file name to appear in the itinerary dashboard.
                <br />
                <br />
                <b>Examples:</b>
                <br />
                <br />
                Andrew Torres x 2
                <br />
                <br />
                West Family x 5
                <br />
                <br />
                Cynthia Bacall, Kenya 2022
                <br />
                <br />
                Smith Christmas 2023
              </Text>
            }
          />
        </>
      )}

      <Field
        component={FormGroupAdapter}
        name="notes"
        placeholder="Internal notes..."
        type="textarea"
        className={styles.textArea}
        noMargin
        minRows={3}
        parse={(value) => (value === '' ? null : value)}
        onFocus={showPopup(
          <Text accent style={{ fontSize: '1.5rem' }}>
            <b>Tool Tip:</b>
            <br />
            <br />
            Enter some internal notes to remind yourself what is included in this itinerary.
            <br />
            <br />
            This will help you find and recognize it easily later.
          </Text>,
        )}
      />
    </>
  );
};

const CoverSection = () => {
  const showPopup = useFormFieldPopup();
  const itinerary = useSelector(itinerariesItemDataSelector);
  const { values } = useFormState();

  const itineraryForPreview = (() => {
    return {
      ...itinerary,
      title: values.title,
      travelDates: values.travelDates,
      preparedFor: values.preparedFor,
    };
  })();

  return (
    <>
      <div className={styles.coverTitleWrapper}>
        <div>
          <Text type="brandon" isStrong weight="500">
            Cover Page
          </Text>

          <Text type="brandon" size="regular" color="gray-600" className={styles.coverSubTitle}>
            This information will appear on the Cover Page.
          </Text>
        </div>

        <GeneralPopoverPagePreview proposal={itineraryForPreview} />
      </div>

      <Field component={FormGroupAdapter} name="inputGroup" type="inputGroup">
        <Field
          component={FormGroupAdapter}
          name="title"
          placeholder="Proposal Title*"
          type="text"
          autoComplete="off"
          onFocus={showPopup(
            <Text accent style={{ fontSize: '1.5rem' }}>
              <b>Tool Tip:</b>
              <br />
              <br />
              Come up with a catchy title for your itinerary proposal.
              <br />
              Keep it short and snappy.
              <br />
              <br />
              <b>Examples:</b>
              <br />
              <br />
              The Best of East Africa
              <br />
              <br />
              A Tailor Made
              <br />
              Honeymoon Safari
              <br />
              <br />
              Culture & Adventure
            </Text>,
          )}
        />
        <Field
          component={FormGroupAdapter}
          name="preparedFor"
          placeholder="Prepared For"
          type="text"
          parse={(value) => value || null}
          onFocus={showPopup(
            <Text accent style={{ fontSize: '1.5rem' }}>
              <b>Tool Tip:</b>
              <br />
              <br />
              Guests’ names.
              <br />
              <br />
              <b>Examples:</b>
              <br />
              <br />
              Mr & Mrs Smith
              <br />
              <br />
              The Doe Family
              <br />
              <br />
              John Smith & Friends
            </Text>,
          )}
        />
      </Field>

      <Text type="brandon" size="regular" className={styles.coverSectionDatesTitle}>
        Dates of travel or approximate travel window.
      </Text>

      <Field
        component={FormGroupAdapter}
        name="travelDates"
        placeholder="Enter Dates"
        type="text"
        parse={(value) => value || null}
        noMargin
        onFocus={showPopup(
          <Text accent style={{ fontSize: '1.5rem' }}>
            <b>Examples:</b>
            <br />
            <br />
            September 2027
            <br />
            <br />
            August 21 -
            <br />
            September 20, 2027
            <br />
            <br />
            Christmas 2028
          </Text>,
        )}
      />
    </>
  );
};

const IncludeSection = () => {
  const { isTabletOrMobile } = useMediaBreakpoints();
  const { values } = useFormState();

  return (
    <>
      <div className={styles.includeTitleWrapper}>
        <Text type="brandon" isStrong weight="500">
          Include Information Pages
        </Text>

        <Text type="brandon" size="regular" color="gray-600" className={styles.coverSubTitle}>
          Select pages from your content library, except Property or Area pages, which should be added from the Details
          tab.
        </Text>
      </div>

      <div className={styles.container}>
        <div className={styles.includeSectionSelectLabel}>
          <Text type="brandon" size="regular">
            After the Cover Page
          </Text>
          &nbsp;
          <Text type="brandon" size="regular" color="gray-600">
            (e.g. About Us page)
          </Text>
        </div>
        <Field
          name="filtersForPagesAfterCoverPage"
          component={() => (
            <ItinerarySearchFilterButton
              isMobile={isTabletOrMobile}
              filter={values?.filtersForPagesAfterCoverPage}
              filterName="Content Pages"
              filterKeyName="filtersForPagesAfterCoverPage"
              className={styles.filterContainer}
              values={values}
            />
          )}
        />
      </div>

      <Field name="aboutSafariPages">
        {(fieldProps) => (
          <FormGroupAdapter withChildren className={styles.includeFormGroup} {...fieldProps}>
            <PageAsyncSelectAdapter
              isMulti
              placeholder="Select Pages"
              params={{ types: pageTypesToSearch, ...values?.filtersForPagesAfterCoverPage?.data }}
              {...fieldProps}
            />
          </FormGroupAdapter>
        )}
      </Field>

      <div className={styles.container}>
        <div className={styles.includeSectionSelectLabel}>
          <Text type="brandon" size="regular">
            Before the Day-by-Day Itinerary
          </Text>
          &nbsp;
          <Text type="brandon" size="regular" color="gray-600">
            (e.g. Introductory Note page)
          </Text>
        </div>
        <Field
          name="filtersForPagesAfterMapPage"
          component={() => (
            <ItinerarySearchFilterButton
              isMobile={isTabletOrMobile}
              filter={values?.filtersForPagesAfterMapPage}
              filterName="Content Pages"
              filterKeyName="filtersForPagesAfterMapPage"
              className={styles.filterContainer}
              values={values}
            />
          )}
        />
      </div>

      <Field name="aboutUsPages">
        {(fieldProps) => (
          <FormGroupAdapter withChildren className={styles.includeFormGroup} {...fieldProps}>
            <PageAsyncSelectAdapter
              isMulti
              placeholder="Select Pages"
              params={{ types: pageTypesToSearch, ...values?.filtersForPagesAfterMapPage?.data }}
              {...fieldProps}
            />
          </FormGroupAdapter>
        )}
      </Field>

      <div className={styles.container}>
        <div className={styles.includeSectionSelectLabel}>
          <Text type="brandon" size="regular">
            End of Itinerary
          </Text>
          &nbsp;
          <Text type="brandon" size="regular" color="gray-600">
            (e.g. Terms & Conditions page)
          </Text>
        </div>

        <Field
          name="filtersForPagesAfterCostsPage"
          component={() => (
            <ItinerarySearchFilterButton
              isMobile={isTabletOrMobile}
              filter={values?.filtersForPagesAfterCostsPage}
              filterName="Content Pages"
              filterKeyName="filtersForPagesAfterCostsPage"
              className={styles.filterContainer}
              values={values}
            />
          )}
        />
      </div>

      <Field name="miscPages">
        {(fieldProps) => (
          <FormGroupAdapter withChildren noMargin {...fieldProps}>
            <PageAsyncSelectAdapter
              isMulti
              placeholder="Select Pages"
              params={{ types: pageTypesToSearch, ...values?.filtersForPagesAfterCostsPage?.data }}
              {...fieldProps}
            />
          </FormGroupAdapter>
        )}
      </Field>
    </>
  );
};

export default GeneralInfo;
